import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS, RESOURCE_LINKS, IMAGES } from "../lib/site";
import { EASE } from "./Motion";

export function Logo() {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
      <img
        src={IMAGES.logoIcon}
        alt=""
        data-testid="logo-icon"
        className="h-11 sm:h-12 w-auto object-contain transition-opacity duration-300 group-hover:opacity-80"
      />
      <img
        src={IMAGES.logoWordmark}
        alt="HAQILA Global Trading"
        data-testid="logo-wordmark"
        className="h-9 sm:h-10 w-auto object-contain transition-opacity duration-300 group-hover:opacity-80"
      />
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const mobileNavClass = (link) => ({ isActive }) => {
    let color = "text-offwhite/50 hover:text-offwhite";
    if (link.to === "/request-a-quote") color = "text-clay";
    else if (isActive) color = "text-offwhite";
    return `block py-3 font-display text-3xl font-bold tracking-tight transition-colors duration-300 ${color}`;
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#111111]/70 border-b border-white/10"
      >
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10 h-[72px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-offwhite" : "text-offwhite/55 hover:text-offwhite"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/request-a-quote"
              data-testid="header-quote-cta"
              className="hidden sm:inline-flex items-center gap-2 bg-forest hover:bg-moss text-offwhite text-[13px] font-semibold tracking-wide px-5 py-2.5 transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              className="lg:hidden w-9 h-9 flex items-center justify-center text-offwhite/80 hover:text-offwhite transition-colors duration-300"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-charcoal pt-[72px] flex flex-col"
          >
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {[...NAV_LINKS, { label: "Request a Quote", to: "/request-a-quote" }].map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={mobileNavClass(link)}
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="px-8 pb-6 flex flex-wrap gap-x-6 gap-y-2"
            >
              {RESOURCE_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs font-semibold uppercase tracking-[0.2em] text-offwhite/40 hover:text-offwhite transition-colors duration-300"
                >
                  {link.label}
                </NavLink>
              ))}
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="px-8 pb-10 text-xs uppercase tracking-[0.25em] text-offwhite/35"
            >
              Indonesia — Sourcing — Export — Global Supply
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
